/**
 * FDI permanent-dentition layout + the controlled condition/surface vocabularies for the odontogram UI
 * (#18636). Conditions MUST match `OdontogramService.CONDITIONS` (backend rejects anything else).
 * Dentist-facing layout: the patient's right side is on the viewer's left, upper arch on top.
 */

/** Upper arch, left→right as the dentist sees it: quadrant 1 (18→11) then quadrant 2 (21→28). */
export const FDI_UPPER: readonly string[] = [
  "18", "17", "16", "15", "14", "13", "12", "11",
  "21", "22", "23", "24", "25", "26", "27", "28",
];

/** Lower arch: quadrant 4 (48→41) then quadrant 3 (31→38). */
export const FDI_LOWER: readonly string[] = [
  "48", "47", "46", "45", "44", "43", "42", "41",
  "31", "32", "33", "34", "35", "36", "37", "38",
];

export interface ConditionMeta {
  value: string;
  label: string;
  /** Tailwind classes for the tooth marker + legend swatch. */
  dot: string;
}

/** The 13 conditions, in clinical-severity-ish order. `dot` classes are literal so Tailwind keeps them. */
export const CONDITIONS: readonly ConditionMeta[] = [
  { value: "SOUND", label: "Bình thường", dot: "bg-slate-100 text-slate-600 border-slate-300" },
  { value: "CARIES", label: "Sâu răng", dot: "bg-red-500 text-white border-red-600" },
  { value: "TO_TREAT", label: "Cần điều trị", dot: "bg-yellow-400 text-yellow-900 border-yellow-500" },
  { value: "TO_EXTRACT", label: "Chỉ định nhổ", dot: "bg-orange-500 text-white border-orange-600" },
  { value: "FILLED", label: "Đã trám", dot: "bg-blue-500 text-white border-blue-600" },
  { value: "ROOT_CANAL", label: "Điều trị tủy", dot: "bg-amber-500 text-white border-amber-600" },
  { value: "SEALANT", label: "Trám bít hố rãnh", dot: "bg-green-500 text-white border-green-600" },
  { value: "CROWN", label: "Mão / chụp", dot: "bg-purple-500 text-white border-purple-600" },
  { value: "BRIDGE", label: "Cầu răng", dot: "bg-indigo-500 text-white border-indigo-600" },
  { value: "VENEER", label: "Mặt dán sứ (veneer)", dot: "bg-pink-500 text-white border-pink-600" },
  { value: "IMPLANT", label: "Cấy ghép (implant)", dot: "bg-teal-500 text-white border-teal-600" },
  { value: "MISSING", label: "Mất răng", dot: "bg-slate-100 text-slate-400 border-slate-200 line-through" },
  { value: "EXTRACTED", label: "Đã nhổ", dot: "bg-slate-600 text-white border-slate-700 line-through" },
];

export const CONDITION_LABEL: Record<string, string> = Object.fromEntries(
  CONDITIONS.map((c) => [c.value, c.label]),
);
export const CONDITION_DOT: Record<string, string> = Object.fromEntries(
  CONDITIONS.map((c) => [c.value, c.dot]),
);

/** Tooth surfaces (FDI notation). "whole" = the whole tooth (mapped to null on submit). */
export const SURFACES: readonly { value: string; label: string }[] = [
  { value: "whole", label: "Cả răng" },
  { value: "M", label: "Gần (Mesial)" },
  { value: "D", label: "Xa (Distal)" },
  { value: "O", label: "Mặt nhai (Occlusal)" },
  { value: "B", label: "Ngoài (Buccal)" },
  { value: "L", label: "Trong (Lingual)" },
];
